import { ComboboxData, Group } from "@mantine/core"
import { IconTrash } from "@tabler/icons-react"
import { ActionIcon, InlineSelectInput } from "@tfg/ui"
import { useEffect, useState } from "react"

import { typeComponent } from "~/pages/Admin/Mapping/components/MappingTable"
import { useDeleteMappingElementMutation } from "~/pages/Admin/Mapping/hooks/useDeleteMappingElementMutation"
import { useUpdateMappingElementMutation } from "~/pages/Admin/Mapping/hooks/useUpdateMappingElementMutation"
import { MappingTypeNames, TypeKeys } from "~/pages/Admin/Mapping/types"
import { showAxiosError } from "~/utils/showAxiosError"

export type SelectProps = {
  typesList: TypeKeys[]
  mappingType: typeComponent
  typeName: MappingTypeNames
  page: number
}

export function filterDeletedAtSelectOptions(
  list: TypeKeys[] | undefined,
  id: number
): ComboboxData {
  if (!list) return []
  return list
    .filter((item) => !item.deletedAt || item.id === id)
    .map((list) => ({
      value: list.id.toString(),
      label: list.key,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

export function MappingSelect({
  typesList,
  mappingType,
  typeName,
  page = 1,
}: SelectProps) {
  const [selectDisabled, setSelectDisabled] = useState<boolean>(false)
  const { mutate: updateMappingType } = useUpdateMappingElementMutation(
    { page },
    {
      onError(error) {
        showAxiosError({
          error: error as Error,
          showNotification: true,
        })
      },
      onMutate() {
        setSelectDisabled(true)
      },
      onSettled() {
        setSelectDisabled(false)
      },
    }
  )
  const { mutate: deleteMappingType } = useDeleteMappingElementMutation({
    page,
  })

  const [options, setOptions] = useState<ComboboxData>([])
  const [value, setValue] = useState<string | null>(
    mappingType.type.typeId ? mappingType.type.typeId.toString() : null
  )

  useEffect(
    () => setOptions(filterDeletedAtSelectOptions(typesList, Number(value))),
    [typesList, value]
  )

  return (
    <Group justify="space-between" wrap="nowrap" w="100%">
      <InlineSelectInput
        options={options}
        value={value}
        onChange={(value: string | null) => {
          setValue(value)
          if (value) {
            updateMappingType({
              data: {
                type: mappingType.type.type,
                typeId: Number(value),
                alias: mappingType.type.alias,
              },
            })
          }
        }}
        selectProps={{
          searchable: true,
          disabled: selectDisabled,
        }}
        label={mappingType.type.alias}
        size="sm"
        cta={
          <ActionIcon
            icon={<IconTrash />}
            onClick={() =>
              deleteMappingType({
                data: {
                  alias: mappingType.type.alias,
                  type: typeName,
                },
              })
            }
          />
        }
      />
    </Group>
  )
}
